/* eslint-disable react/prop-types */
/* eslint-disable max-len */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Menu, Switch, FormControlLabel } from '@mui/material';
import { Filter } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import DataTable from '../../components/DataTable';
import StatusPill from '../../components/StatusPill';
import MemberCell from './MemberCell';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';
import ViewProfileRequestPopup from './viewProfileRequestPopup';

const PAGE_SIZE = 10;
const STATUS_TONE = { approved: 'success', pending: 'warning', rejected: 'error' };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const dash = <span className="text-ink-500">—</span>;

function ProfileUpdateRequests() {
  const { search } = useLocation();
  const { activeWorkspace } = useContext(UserContext);
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const history = useHistory();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedProfileRequest, setSelectedProfileRequest] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [profileUpdateRequests, setProfileUpdateRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [statusValue, setStatusValue] = useState('Pending'); // Pending | Approved | Rejected | All
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const onClickView = (profileRequest) => {
    setSelectedProfileRequest(profileRequest);
    setIsOpenModal(true);
  };

  const handleClose = () => setIsOpenModal(false);

  const handlePageChange = (next) => {
    setCurrentPage(next);
    history.push(`?page=${next}`);
  };

  const buildRequestParams = (params = {}) => ({
    page: currentPage,
    ...params,
    status: showAllRequests || statusValue === 'All' ? null : [statusValue],
  });

  const getProfileUpdateRequests = async (params) => {
    setIsLoading(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getProfileUpdateRequests(activeWorkspace.id),
      params: { pageSize: PAGE_SIZE, ...params },
    });
    if (response.success) {
      setProfileUpdateRequests(Array.isArray(response.data) ? response.data : []);
    }
    setIsLoading(false);
  };

  const handleFilterClose = () => setAnchorEl(null);
  const handleFilterClick = (event) => setAnchorEl(event.currentTarget);

  const handleReset = () => {
    setStatusValue('Pending');
    setShowAllRequests(false);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    history.push('?page=1');
    getProfileUpdateRequests(buildRequestParams({ page: 1 }));
    handleFilterClose();
  };

  const handleToggleShowAll = (_, checked) => {
    setShowAllRequests(checked);
    setCurrentPage(1);
    history.push('?page=1');
    getProfileUpdateRequests({ page: 1, status: checked ? null : [statusValue] });
  };

  const handleApproval = async (status) => {
    setIsUpdating(true);
    const response = await sendRequest({
      method: 'put',
      data: { status },
      url: API.WORKSPACE.profileUpdateApprovalByManager(selectedProfileRequest?.id),
    });
    setIsUpdating(false);
    if (response.success) {
      setIsOpenModal(false);
      getProfileUpdateRequests(buildRequestParams({ page: currentPage }));
    }
  };

  // Fetch once; the API returns the full set (no server paging), so pagination
  // is handled client-side below. Filter/toggle/approval refetch explicitly.
  useEffect(() => {
    getProfileUpdateRequests(buildRequestParams({ page: currentPage }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pagedRows = profileUpdateRequests.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const columns = [
    {
      key: 'member',
      label: 'Member',
      width: 260,
      flex: true,
      render: (r) => (
        <MemberCell
          id={r?.member?.id}
          firstName={r?.member?.user?.firstName}
          lastName={r?.member?.user?.lastName}
          email={r?.member?.user?.email}
          profilePicture={r?.member?.user?.profilePicture}
        />
      ),
    },
    {
      key: 'field',
      label: 'Profile Field',
      width: 150,
      render: (r) => <span className="text-ink-900">{r.profile_field}</span>,
    },
    {
      key: 'manager',
      label: 'Manager',
      width: 160,
      flex: true,
      render: (r) => r?.member?.Manager?.user?.firstName || dash,
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (r) => <StatusPill tone={STATUS_TONE[r.status] || 'neutral'}>{cap(r.status)}</StatusPill>,
    },
    {
      key: 'actions',
      label: 'Action',
      width: 150,
      align: 'center',
      render: (r) => (
        <button
          type="button"
          onClick={() => onClickView(r)}
          data-testid="view-request"
          className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition whitespace-nowrap"
          style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}
        >
          View Request
        </button>
      ),
    },
  ];

  return (
    <>
      {/* Header: title + controls (Manage Members layout) */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-5">
        <div className="min-w-0">
          <h1
            className="font-bold text-ink-900 leading-tight truncate text-2xl lg:text-[32px] lg:leading-10"
            data-testid="title"
            data-tour="profile-requests"
          >
            Profile Update Requests
          </h1>
          <p className="text-sm text-ink-500 mt-1" data-testid="subtitle">
            Verify and approve profile update requests from your team members
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={(
              <Switch
                color="primary"
                checked={showAllRequests}
                onChange={handleToggleShowAll}
                inputProps={{ 'aria-label': 'show all profile update requests' }}
              />
            )}
            label={<span className="text-sm text-ink-700 whitespace-nowrap">Show all requests</span>}
          />
          <button
            type="button"
            onClick={handleFilterClick}
            data-testid="filter-search"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] text-sm font-semibold text-white bg-brand-700 hover:bg-[#1a3156] transition"
          >
            <Filter size={16} />
            Filters
          </button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: '8px',
                borderRadius: '12px',
                border: '1px solid #e0e1e6',
                boxShadow: '0 8px 24px rgba(14, 30, 64, 0.12)',
                width: 360,
                maxWidth: 'calc(100vw - 32px)',
              },
            }}
            sx={{ '& .MuiMenu-list': { padding: 0 } }}
          >
            <div style={{ padding: 24 }}>
              <h5 className="text-lg font-semibold text-ink-900 mb-4">Filters</h5>
              <div className="mb-5">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5" style={{ letterSpacing: '0.05em' }}>
                  Status
                </span>
                <select
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                  data-testid="status-filter"
                  className={`${STYLED_SELECT_CLASS} w-full`}
                  style={STYLED_SELECT_STYLE}
                >
                  <option value="All">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div className="flex justify-end items-center gap-4">
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-sm font-medium text-ink-900 hover:text-ink-700 bg-transparent border-0 cursor-pointer"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSearch}
                  className="inline-flex items-center justify-center px-5 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] transition"
                >
                  Search
                </button>
              </div>
            </div>
          </Menu>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={pagedRows}
        getRowKey={(r) => r.id}
        loading={isLoading}
        emptyText="No profile update requests found."
        page={currentPage}
        pageSize={PAGE_SIZE}
        total={profileUpdateRequests.length}
        onPageChange={handlePageChange}
        minWidth={900}
      />

      {isOpenModal && (
        <ViewProfileRequestPopup
          selectedRequest={selectedProfileRequest}
          isOpen={isOpenModal}
          handleClose={handleClose}
          handleApproval={handleApproval}
          isUpdating={isUpdating}
        />
      )}
    </>
  );
}

export default ProfileUpdateRequests;
